import { useCallback, useEffect, useRef, useState, type PointerEvent } from "react"
import { cn } from "@/lib/utils"

export type AskAIPillMode = "expanded" | "docked-left" | "docked-right"

export interface AskAIPillPosition {
  x: number
  y: number
}

export interface AskAIPillProps {
  onOpen?: () => void
  hidden?: boolean
  label?: string
  iconSlot?: React.ReactNode
  /** Small indicator rendered at the pill's top-right corner. */
  badge?: React.ReactNode
  id?: string
  persistKey?: string
  className?: string
}

const DRAG_THRESHOLD = 6
const EDGE_PAD = 16
const EDGE_SNAP = 60
const PILL_APPROX_W = 92
const PILL_APPROX_H = 44
const DOCKED_W = 44
const COMPACT_SIZE = 44

function clamp(x: number, min: number, max: number) {
  return Math.min(max, Math.max(min, x))
}

function loadState(
  key: string,
): { pos: AskAIPillPosition; mode: AskAIPillMode } | null {
  try {
    if (typeof sessionStorage === "undefined") return null
    const raw = sessionStorage.getItem(key)
    if (!raw) return null
    const p = JSON.parse(raw)
    if (typeof p?.pos?.x !== "number" || typeof p?.pos?.y !== "number") return null
    const mode =
      p.mode === "docked-left" || p.mode === "docked-right" ? p.mode : "expanded"
    return { pos: p.pos, mode }
  } catch {
    return null
  }
}

function saveState(key: string, pos: AskAIPillPosition, mode: AskAIPillMode) {
  try {
    if (typeof sessionStorage === "undefined") return
    sessionStorage.setItem(key, JSON.stringify({ pos, mode }))
  } catch {
    /* ignore */
  }
}

function DefaultMark({ size = 18 }: { size?: number }) {
  return (
    <svg
      height={size}
      width={size}
      viewBox="0 0 36 30"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className="block shrink-0"
    >
      <path d="M11.6121 0.820068L22.1729 29.5739L10.8749 23.86L0 29.7583L11.6121 0.820068Z" />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M27.3898 0.922175L14.1338 1.05161L24.4995 29.5538L29.4995 29.0495C33.0278 28.9303 35.7793 26.0791 35.6423 22.0244C35.5196 18.393 33.1236 15.4549 30.0901 14.9666C32.6611 13.8797 34.4308 11.2072 34.3274 8.1452C34.2007 4.39666 31.5208 1.09861 28.0001 0.921521L27.9994 0.916223L27.8967 0.917226C27.7654 0.912926 27.633 0.912962 27.4995 0.917471C27.4629 0.918708 27.4263 0.920277 27.3898 0.922175Z"
      />
    </svg>
  )
}

export function AskAIPill({
  onOpen,
  hidden = false,
  label = "Ask AI",
  iconSlot,
  badge,
  id = "ask-ai-pill",
  persistKey = "v1.askai.pill.pos",
  className,
}: AskAIPillProps) {
  const [pos, setPos] = useState<AskAIPillPosition | null>(
    () => loadState(persistKey)?.pos ?? null,
  )
  const [mode, setMode] = useState<AskAIPillMode>(
    () => loadState(persistKey)?.mode ?? "expanded",
  )
  const [dragging, setDragging] = useState(false)
  const dragStart = useRef<{
    x: number
    y: number
    originX: number
    originY: number
    pointerId: number
  } | null>(null)
  const didDrag = useRef(false)
  const pillRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (typeof window === "undefined") return
    const vw = window.innerWidth
    const vh = window.innerHeight
    setPos((p) => {
      if (!p) {
        return {
          x: vw - PILL_APPROX_W - EDGE_PAD,
          y: vh - PILL_APPROX_H - EDGE_PAD,
        }
      }
      const w = mode === "expanded" ? PILL_APPROX_W : DOCKED_W
      let nextX
      if (mode === "docked-left") nextX = 0
      else if (mode === "docked-right") nextX = vw - w
      else nextX = clamp(p.x, EDGE_PAD, vw - w - EDGE_PAD)
      return { x: nextX, y: clamp(p.y, EDGE_PAD, vh - PILL_APPROX_H - EDGE_PAD) }
    })
    // run once on mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onPointerDown = useCallback(
    (e: PointerEvent<HTMLButtonElement>) => {
      e.preventDefault()
      didDrag.current = false
      dragStart.current = {
        x: e.clientX,
        y: e.clientY,
        originX: pos?.x ?? 0,
        originY: pos?.y ?? 0,
        pointerId: e.pointerId,
      }
    },
    [pos],
  )

  const onPointerMove = useCallback(
    (e: PointerEvent<HTMLButtonElement>) => {
      if (!dragStart.current) return
      const dx = e.clientX - dragStart.current.x
      const dy = e.clientY - dragStart.current.y
      if (!dragging) {
        if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return
        setDragging(true)
        didDrag.current = true
        try {
          pillRef.current?.setPointerCapture(dragStart.current.pointerId)
        } catch {
          /* ignore */
        }
      }
      setPos({
        x: clamp(
          dragStart.current.originX + dx,
          0,
          window.innerWidth - COMPACT_SIZE,
        ),
        y: clamp(
          dragStart.current.originY + dy,
          EDGE_PAD,
          window.innerHeight - PILL_APPROX_H - EDGE_PAD,
        ),
      })
    },
    [dragging],
  )

  const onPointerUp = useCallback(
    (e: PointerEvent<HTMLButtonElement>) => {
      const wasDragging = dragging
      if (wasDragging && pos) {
        const vw = window.innerWidth
        const vh = window.innerHeight
        const y = clamp(pos.y, EDGE_PAD, vh - PILL_APPROX_H - EDGE_PAD)
        const centerX = pos.x + COMPACT_SIZE / 2
        let nextMode: AskAIPillMode
        let nextPos: AskAIPillPosition
        if (centerX < EDGE_SNAP) {
          nextMode = "docked-left"
          nextPos = { x: 0, y }
        } else if (centerX > vw - EDGE_SNAP) {
          nextMode = "docked-right"
          nextPos = { x: vw - DOCKED_W, y }
        } else {
          nextMode = "expanded"
          nextPos = {
            x: clamp(pos.x, EDGE_PAD, vw - PILL_APPROX_W - EDGE_PAD),
            y,
          }
        }
        setPos(nextPos)
        setMode(nextMode)
        saveState(persistKey, nextPos, nextMode)
        setDragging(false)
        try {
          pillRef.current?.releasePointerCapture(e.pointerId)
        } catch {
          /* ignore */
        }
      }
      if (!wasDragging && !didDrag.current) {
        if ((mode === "docked-left" || mode === "docked-right") && pos) {
          const vw = window.innerWidth
          const nextPos: AskAIPillPosition = {
            x: mode === "docked-left" ? EDGE_PAD : vw - PILL_APPROX_W - EDGE_PAD,
            y: pos.y,
          }
          setPos(nextPos)
          setMode("expanded")
          saveState(persistKey, nextPos, "expanded")
        } else {
          onOpen?.()
        }
      }
      dragStart.current = null
    },
    [dragging, pos, mode, onOpen, persistKey],
  )

  if (!pos) return null

  const isDockedLeft = mode === "docked-left" && !dragging
  const isDockedRight = mode === "docked-right" && !dragging
  const isDocked = isDockedLeft || isDockedRight
  const showText = mode === "expanded" && !dragging

  return (
    <button
      ref={pillRef}
      id={id}
      type="button"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{
        left: `${pos.x}px`,
        top: `${pos.y}px`,
        width: dragging
          ? `${COMPACT_SIZE}px`
          : isDocked
            ? `${DOCKED_W}px`
            : undefined,
      }}
      aria-label={isDocked ? `Expand ${label}` : label}
      aria-hidden={hidden ? "true" : undefined}
      tabIndex={hidden ? -1 : 0}
      className={cn(
        "fixed z-40 select-none inline-flex items-center justify-center touch-none",
        "h-11 bg-foreground text-background text-label font-semibold tracking-wide shadow-lg",
        "motion-safe:transition-[left,top,width,transform,border-radius,padding]",
        "motion-safe:duration-200 motion-safe:ease-out",
        "active:scale-[0.97]",
        dragging && "rounded-full gap-0 px-0 cursor-grabbing scale-105",
        !dragging && mode === "expanded" && "rounded-full gap-2 px-5 cursor-grab",
        isDockedLeft && "rounded-l-none rounded-r-full gap-0 px-0 cursor-pointer",
        isDockedRight && "rounded-r-none rounded-l-full gap-0 px-0 cursor-pointer",
        hidden && "invisible pointer-events-none",
        className,
      )}
    >
      {iconSlot ?? <DefaultMark size={showText ? 16 : 18} />}
      {showText && <span>{label}</span>}
      {badge && (
        <span className="absolute -top-0.5 -right-0.5">{badge}</span>
      )}
    </button>
  )
}
